// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateLoadParameter from "@insite/client-framework/Store/Pages/MyLists/Handlers/UpdateLoadParameter";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import { IconWrapper } from "@insite/mobius/Icon";
import Select, { SelectProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const SelectedWishListTypesCookieName = "SelectedWishListTypes";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    return {
        query: state.pages.myLists.getWishListsParameter.query,
        intiallySelectedListTypes: (getCookie(SelectedWishListTypesCookieName) || "IsSaved,IsSingleUse").split(","),
    };
};

const mapDispatchToProps = {
    updateLoadParameter,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface MyListsFilterByListTypeStyles {
    wrapper?: InjectableCss;
    select?: SelectProps;
}

export const myListsFilterByListTypeStyles: MyListsFilterByListTypeStyles = {
    select: {
        labelProps: {
            css: css`
                font-size: 14px;
                line-height: 22px;
                margin-right: 8px;
                padding: 0;
                font-weight: 400;
                width: unset;
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                width: 150px;
                &&& {
                    ${IconWrapper} {
                        height: 29px;
                    }
                    select {
                        height: 29px;
                    }
                }
            `,
            inputSelect: css`
                font-size: 14px;
                line-height: 17px;
            `,
            formField: css`
                align-items: center;
            `,
        },
    },
};

const styles = myListsFilterByListTypeStyles;

const filterChangeHandler = (event: React.FormEvent<HTMLSelectElement>, props: Props) => {
    props.updateLoadParameter({ listType: event.currentTarget.value } as any);
};

const MyListsFilterByListType: React.FunctionComponent<Props> = props => {
    const [listTypes, setListTypes] = React.useState<string[]>(props.intiallySelectedListTypes);

    React.useEffect(() => {
        props.updateLoadParameter({ listType: props.intiallySelectedListTypes.join(",") } as any);
    }, []);

    React.useEffect(() => {
        const newSelectedListTypes = listTypes.join(",");
        props.updateLoadParameter({ listType: newSelectedListTypes } as any);
        setCookie(SelectedWishListTypesCookieName, newSelectedListTypes);
    }, [listTypes]);

    const handleChange = (type: string) => (ev: any, checked: boolean) => {
        if (checked) {
            setListTypes(listTypes.concat(type));
        } else {
            setListTypes(listTypes.filter(t => t !== type));
        }
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <CheckboxGroup label={translate("List Type")}>
                <Checkbox checked={listTypes.includes("IsSaved")} onChange={handleChange("IsSaved")}>
                    {translate("Saved")}
                </Checkbox>
                <Checkbox checked={listTypes.includes("IsSingleUse")} onChange={handleChange("IsSingleUse")}>
                    {translate("Single-Use")}
                </Checkbox>
                <Checkbox checked={listTypes.includes("JobTemplates")} onChange={handleChange("JobTemplates")}>
                    {translate("Shared by PSC")}
                </Checkbox>
            </CheckboxGroup>
            {/* <Select
                data-test-selector="myLists_filter"
                {...styles.select}
                onChange={(event: React.FormEvent<HTMLSelectElement>) => filterChangeHandler(event, props)}
                label={translate("List Type")}
                labelPosition="left"
                sizeVariant="small"
            >
                <option value="">{translate("All")}</option>
                <option value="IsSaved">{translate("Saved")}</option>
                <option value="IsSingleUse">{translate("Single-Use")}</option>
                <option value="JobTemplates">{translate("Shared by PSC")}</option>
            </Select> */}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyListsFilterByListType),
    definition: {
        group: "My Lists",
        displayName: "Filter List Type",
        allowedContexts: ["MyListsPage"],
    },
};

export default widgetModule;
